import { ACCB_URL, ACCB_LEGACY_URL } from "app/constants";

/**
 * Central trust boundary for URLs that reach the browser inside an API
 * response — notice downloads, presigned document links, scrutiny previews.
 *
 * Every one of those URLs is attacker-influenced data until it has been
 * checked: a compromised or buggy upstream can hand the UI a `javascript:`
 * payload, a plain-http downgrade, or a link to a host that harvests the
 * session-bearing query string it is navigated with. `validateOrigin` is the
 * single place that decision is made, so it can only be made one way.
 */

// Only these schemes may ever be handed to window.open / location / iframe.src.
// Everything else — javascript:, data:, vbscript:, file: — is a code-execution
// or exfiltration vector dressed up as a link.
const ALLOWED_PROTOCOLS = ["https:", "http:"];

// Exact hosts BAC documents are legitimately served from.
const TRUSTED_HOSTS = [
  ACCB_URL,
  ACCB_LEGACY_URL,
  "storage.googleapis.com",
];

// Suffix rules, always matched with the leading dot so that a look-alike host
// such as "evil-webnyay.in" cannot pass as a subdomain of "webnyay.in".
const TRUSTED_HOST_SUFFIXES = [
  `.${ACCB_URL}`,
  `.${ACCB_LEGACY_URL}`,
  // Webnyay main backend and the document-management service
  ".webnyay.in",
  ".webnyay.ai",
  // Cloud storage presigned downloads (virtual-hosted bucket addressing)
  ".storage.googleapis.com",
  ".amazonaws.com",
];

// Loopback is only trusted for local development, where the app itself is
// served over plain http and the backend runs alongside it.
const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1", "[::1]"];

const isLocalHostname = (hostname) => LOCAL_HOSTNAMES.includes(hostname);

const isTrustedHostname = (hostname) => {
  const host = String(hostname || "").toLowerCase();
  if (!host) return false;
  if (TRUSTED_HOSTS.includes(host)) return true;
  return TRUSTED_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
};

/**
 * Validate that `url` points at a trusted origin.
 *
 * @param {string} url raw value from an API response
 * @returns {string|null} the absolute URL to use, or null if it must be
 *   refused. Callers must treat null as "do not navigate".
 */
export const validateOrigin = (url) => {
  if (!url || typeof url !== "string") return null;

  let parsed;
  try {
    // The base makes relative same-origin paths ("/media/x.pdf") resolvable;
    // an absolute URL in `url` ignores it.
    parsed = new URL(url, window.location.origin);
  } catch (err) {
    return null;
  }

  if (!ALLOWED_PROTOCOLS.includes(parsed.protocol)) return null;

  // Same-origin is always fine — it is the app talking to itself.
  if (parsed.origin === window.location.origin) return parsed.href;

  if (isLocalHostname(parsed.hostname)) {
    return isLocalHostname(window.location.hostname) ? parsed.href : null;
  }

  // Cross-origin links must be both trusted and encrypted: a document URL
  // carries its own authorisation in the query string, so http would put it
  // on the wire in clear text.
  if (parsed.protocol !== "https:") return null;
  if (!isTrustedHostname(parsed.hostname)) return null;

  return parsed.href;
};

export const isTrustedUrl = (url) => validateOrigin(url) !== null;

const DEFAULT_REFUSAL_MESSAGE =
  "This document link could not be verified and was not opened. Please try again or contact support.";

// Logged rather than shown: the raw URL is the useful forensic detail, and it
// is not something to put in front of the user.
const reportRefusal = (url, context) => {
  console.error(
    `Blocked navigation to an untrusted URL${context ? ` (${context})` : ""}:`,
    url
  );
};

/**
 * Open a validated URL in a new tab.
 *
 * `noopener` severs window.opener so the opened page cannot rewrite this tab
 * (tabnabbing); `noreferrer` keeps the current URL — case ids and all — out of
 * the Referer header.
 *
 * @returns {boolean} whether the URL was opened
 */
export const openTrustedUrl = (url, { onError, context } = {}) => {
  const safeUrl = validateOrigin(url);
  if (!safeUrl) {
    reportRefusal(url, context);
    if (onError) onError(DEFAULT_REFUSAL_MESSAGE);
    return false;
  }
  window.open(safeUrl, "_blank", "noopener,noreferrer");
  return true;
};

/**
 * Navigate the current tab to a validated URL — used for download endpoints
 * that respond with Content-Disposition: attachment.
 *
 * @returns {boolean} whether the navigation happened
 */
export const navigateToTrustedUrl = (url, { onError, context } = {}) => {
  const safeUrl = validateOrigin(url);
  if (!safeUrl) {
    reportRefusal(url, context);
    if (onError) onError(DEFAULT_REFUSAL_MESSAGE);
    return false;
  }
  window.location.assign(safeUrl);
  return true;
};

// How long a hidden download iframe stays attached before it is torn down.
const DOWNLOAD_IFRAME_LIFETIME_MS = 5000;

/**
 * Trigger a background download through a hidden iframe, used when several
 * documents have to be fetched at once and only one of them can own the tab.
 *
 * @returns {boolean} whether the download was started
 */
export const downloadTrustedUrlInIframe = (url, { onError, context } = {}) => {
  const safeUrl = validateOrigin(url);
  if (!safeUrl) {
    reportRefusal(url, context);
    if (onError) onError(DEFAULT_REFUSAL_MESSAGE);
    return false;
  }
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = safeUrl;
  document.body.appendChild(iframe);
  setTimeout(() => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }, DOWNLOAD_IFRAME_LIFETIME_MS);
  return true;
};

const EXTERNAL_REFUSAL_MESSAGE =
  "This link could not be opened because it is not a valid web address.";

/**
 * Open a genuinely third-party link — a video-conference meeting URL, say —
 * where an origin allowlist cannot apply because the host legitimately belongs
 * to someone else.
 *
 * The origin is not checked, so this is the weaker of the two doors: use it
 * only for links the user is meant to leave the app for, never for document or
 * download URLs (those go through `openTrustedUrl`). The scheme is still
 * restricted to https, which is what stops a `javascript:` or `data:` payload
 * from arriving disguised as a meeting link, and noopener/noreferrer still keep
 * the destination from reaching back into this tab or reading its URL.
 *
 * @returns {boolean} whether the URL was opened
 */
export const openExternalUrl = (url, { onError, context } = {}) => {
  let parsed = null;
  if (url && typeof url === "string") {
    try {
      parsed = new URL(url, window.location.origin);
    } catch (err) {
      parsed = null;
    }
  }

  if (!parsed || parsed.protocol !== "https:") {
    reportRefusal(url, context);
    if (onError) onError(EXTERNAL_REFUSAL_MESSAGE);
    return false;
  }

  window.open(parsed.href, "_blank", "noopener,noreferrer");
  return true;
};
